/**
 * customerGrowth — growth-rate regressions + cliff projections for Play 16.
 *
 * Two slopes, both anchored on `customer_link.linked_at` (active links only):
 *   1. Customer-count slope — projects when we hit account cap (25)
 *   2. Total-disk slope — projects when we hit physical disk cap (50GB)
 *
 * Cliff = whichever projection arrives first. The Play 16 Card B surfaces
 * the earlier date in its warning banner.
 *
 * **Anchor decision (Architect §D.1):**
 *   We use `linked_at`, NOT `created_at`. linked_at = when the customer
 *   became active on WHM. created_at = portal sign-up (includes users who
 *   never linked). For substrate-capacity questions, linked_at is the
 *   right anchor.
 *
 * **Data-quality guard (Architect §D.1):**
 *   Show "projection unreliable" warning when fewer than 30 distinct
 *   linked_at days exist, OR when >50% of customers were linked on a
 *   single day (the back-fill import-batch pattern from 2026-04-30).
 */

import { supabase } from '@/shared/lib/supabase';
import { RESELLER_SUBSTRATE } from '@/config/resellerSubstrate';

export interface GrowthProjection {
  /** Whether the dataset is large + spread enough for the projection to mean anything. */
  reliable: boolean;
  /** Reason why projection isn't reliable, if reliable=false. */
  unreliableReason?: string;
  /** Current customer count (active customer_link rows). */
  customerCount: number;
  /** Slope: customers / day (e.g. 0.07 = ~2.1/month). */
  customerSlopePerDay: number;
  /** Slope: MB / day (total disk used across all accounts). */
  diskSlopeMbPerDay: number;
  /** Current total disk used (MB). */
  totalDiskUsedMb: number;
  /** Projection: ISO date when customer count hits accountCap (50 on reseller_medium since 2026-09-10). Null if unreachable. */
  customerCapDate: string | null;
  /** Projection: ISO date when disk used hits diskCapMb (100GB on reseller_medium since 2026-09-10). Null if unreachable. */
  diskCapDate: string | null;
  /** The earlier of customerCapDate / diskCapDate. UI surfaces this in the warning banner. */
  earlierCliffDate: string | null;
  /** Which dimension hits first. */
  earlierCliffDimension: 'customers' | 'disk' | null;
  /** Per-milestone projected dates for the calendar card. */
  milestones: {
    planAheadDate: string | null; // 18 customers
    decideNowDate: string | null; // 25 customers
    coLocateDate: string | null; // 40 customers
  };
}

interface CustomerRow {
  linked_at: string;
}

interface SnapshotDayRow {
  snapshot_date: string;
  total_disk_used_mb: number;
}

/**
 * Linear regression y = a + b·x. Returns slope `b` and intercept `a`.
 * x is days-since-epoch (integer); y is the metric.
 */
function linearRegression(points: Array<{ x: number; y: number }>): { slope: number; intercept: number } {
  if (points.length < 2) return { slope: 0, intercept: points[0]?.y ?? 0 };
  const n = points.length;
  const sumX = points.reduce((s, p) => s + p.x, 0);
  const sumY = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0);
  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return { slope: 0, intercept: sumY / n };
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}

function isoToDaysSinceEpoch(iso: string): number {
  return Math.floor(new Date(iso).getTime() / (1000 * 60 * 60 * 24));
}

function daysSinceEpochToISO(days: number): string {
  return new Date(days * 1000 * 60 * 60 * 24).toISOString().slice(0, 10);
}

function projectDate(currentY: number, targetY: number, slopePerDay: number): string | null {
  if (slopePerDay <= 0) return null; // No growth → never reached
  if (currentY >= targetY) return new Date().toISOString().slice(0, 10); // Already past
  const daysNeeded = (targetY - currentY) / slopePerDay;
  const targetEpochDay = Math.floor(Date.now() / (1000 * 60 * 60 * 24)) + daysNeeded;
  return daysSinceEpochToISO(targetEpochDay);
}

export const customerGrowthService = {
  /**
   * Compute customer-count + disk slopes over the last 90 days and project
   * cliff arrival dates. Single round-trip-ish: two parallel queries.
   */
  async getProjection(): Promise<GrowthProjection> {
    const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

    // Two parallel queries
    const [customersRes, snapshotsRes] = await Promise.all([
      supabase
        .from('customer_link')
        .select('linked_at')
        .is('unlinked_at', null)
        .order('linked_at', { ascending: true }),
      supabase
        .from('usage_snapshot')
        .select('snapshot_date, disk_used_mb')
        .gte('snapshot_date', ninetyDaysAgo)
        .order('snapshot_date', { ascending: true }),
    ]);

    if (customersRes.error) throw new Error(`customer_link load failed: ${customersRes.error.message}`);
    if (snapshotsRes.error) throw new Error(`usage_snapshot load failed: ${snapshotsRes.error.message}`);

    const customers = (customersRes.data ?? []) as CustomerRow[];
    const customerCount = customers.length;

    // Build daily customer-count cumulative series, then take last 90 days.
    const customerByDay = new Map<string, number>();
    let runningCount = 0;
    for (const c of customers) {
      runningCount++;
      const day = c.linked_at.slice(0, 10);
      customerByDay.set(day, runningCount);
    }

    // Densify across the last 90 days (forward-fill missing days)
    const customerPoints: Array<{ x: number; y: number }> = [];
    const todayEpoch = isoToDaysSinceEpoch(new Date().toISOString().slice(0, 10));
    const startEpoch = isoToDaysSinceEpoch(ninetyDaysAgo);
    let lastCount = 0;
    // initial backfill — how many customers existed before the 90-day window
    for (const c of customers) {
      if (isoToDaysSinceEpoch(c.linked_at.slice(0, 10)) < startEpoch) lastCount++;
    }
    for (let d = startEpoch; d <= todayEpoch; d++) {
      const iso = daysSinceEpochToISO(d);
      const dayVal = customerByDay.get(iso);
      if (dayVal !== undefined) lastCount = dayVal;
      customerPoints.push({ x: d, y: lastCount });
    }

    // Data-quality guard — distinct linked_at days within the dataset
    const distinctLinkedDays = new Set(customers.map((c) => c.linked_at.slice(0, 10)));
    const linkedDayCounts = new Map<string, number>();
    for (const c of customers) {
      const day = c.linked_at.slice(0, 10);
      linkedDayCounts.set(day, (linkedDayCounts.get(day) ?? 0) + 1);
    }
    const maxSameDayCount = Math.max(0, ...Array.from(linkedDayCounts.values()));
    const sameDayPct = customerCount > 0 ? (maxSameDayCount / customerCount) * 100 : 0;

    let reliable = true;
    let unreliableReason: string | undefined;
    if (distinctLinkedDays.size < 30) {
      reliable = false;
      unreliableReason = `Only ${distinctLinkedDays.size} distinct customer-onboarding days in dataset (need ≥30).`;
    } else if (sameDayPct > 50) {
      reliable = false;
      unreliableReason = `${sameDayPct.toFixed(0)}% of customers linked on the same day — looks like a back-fill batch, not organic growth.`;
    }

    // Customer-count regression
    const { slope: customerSlopePerDay } = linearRegression(customerPoints);

    // Disk-used regression — sum per day across all accounts
    const diskByDay = new Map<string, number>();
    for (const row of (snapshotsRes.data ?? []) as Array<{ snapshot_date: string; disk_used_mb: number | null }>) {
      const cur = diskByDay.get(row.snapshot_date) ?? 0;
      diskByDay.set(row.snapshot_date, cur + (row.disk_used_mb ?? 0));
    }
    const diskPoints: Array<{ x: number; y: number }> = Array.from(diskByDay.entries()).map(([iso, total]) => ({
      x: isoToDaysSinceEpoch(iso),
      y: total,
    }));
    const { slope: diskSlopeMbPerDay } = linearRegression(diskPoints);
    const totalDiskUsedMb = diskPoints.length > 0 ? diskPoints[diskPoints.length - 1]!.y : 0;

    // Projections
    const cap = RESELLER_SUBSTRATE.currentPlan;
    const customerCapDate = projectDate(customerCount, cap.accountCap, customerSlopePerDay);
    const diskCapDate = projectDate(totalDiskUsedMb, cap.diskCapMb, diskSlopeMbPerDay);

    let earlierCliffDate: string | null = null;
    let earlierCliffDimension: 'customers' | 'disk' | null = null;
    if (customerCapDate && diskCapDate) {
      earlierCliffDate = customerCapDate < diskCapDate ? customerCapDate : diskCapDate;
      earlierCliffDimension = customerCapDate < diskCapDate ? 'customers' : 'disk';
    } else if (customerCapDate) {
      earlierCliffDate = customerCapDate;
      earlierCliffDimension = 'customers';
    } else if (diskCapDate) {
      earlierCliffDate = diskCapDate;
      earlierCliffDimension = 'disk';
    }

    const m = RESELLER_SUBSTRATE.milestones;
    return {
      reliable,
      unreliableReason,
      customerCount,
      customerSlopePerDay,
      diskSlopeMbPerDay,
      totalDiskUsedMb,
      customerCapDate,
      diskCapDate,
      earlierCliffDate,
      earlierCliffDimension,
      milestones: {
        planAheadDate: projectDate(customerCount, m.comfortableMax + 1, customerSlopePerDay), // comfortableMax + 1
        decideNowDate: projectDate(customerCount, m.planAheadMax + 1, customerSlopePerDay), // 23
        coLocateDate: projectDate(customerCount, m.resellerScaleUpMax + 1, customerSlopePerDay), // 40
      },
    };
  },
};
